import { InventoryData, Store } from '../types/inventory';

const API_BASE_URL = '';  // Empty string for relative paths, will use current domain

async function loadStoreData(filename: string): Promise<Store> {
    const url = `${API_BASE_URL}/invdata/${filename}`;
    console.log('Attempting to fetch store data from:', url);
    
    const response = await fetch(url);
    
    if (!response.ok) {
        const errorDetails = {
            status: response.status,
            statusText: response.statusText,
            url: url,
            headers: Object.fromEntries(response.headers.entries())
        };
        console.error('Server response not OK:', errorDetails);
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Validate the response data structure
    if (!data || typeof data !== 'object') {
        throw new Error('Invalid response data: expected an object');
    }
    
    if (!data.store || typeof data.store !== 'object') {
        console.error('Invalid store data:', data.store);
        throw new Error('Invalid response data: store must be an object');
    }
    
    if (!Array.isArray(data.store.products)) {
        console.error('Invalid products data:', data.store.products);
        throw new Error('Invalid response data: store.products must be an array');
    }
    
    console.log('Successfully loaded store data:', {
        storeName: data.store.name,
        productCount: data.store.products.length
    });
    
    return data.store;
}

async function getInventoryFiles(): Promise<string[]> {
    try {
        const response = await fetch(`${API_BASE_URL}/invdata/files`);
        if (!response.ok) {
            throw new Error(`Failed to fetch inventory files: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        return data.files;
    } catch (error) {
        console.error('Error fetching inventory files:', error);
        throw error;
    }
}

export const loadInventoryData = async (): Promise<InventoryData> => {
    try {
        // Dynamically get all available inventory files
        const files = await getInventoryFiles();
        console.log('Found inventory files:', files);
        
        if (files.length === 0) {
            throw new Error('No inventory files found');
        }
        
        // Load all stores data
        const stores = await Promise.all(
            files.map(filename => loadStoreData(filename))
        );
        
        console.log('Successfully loaded all store data:', {
            storeCount: stores.length,
            stores: stores.map(store => ({
                name: store.name,
                productCount: store.products.length
            }))
        });
        
        return { stores };
    } catch (error: unknown) {
        // Enhanced error logging
        const errorInfo = {
            name: error instanceof Error ? error.name : 'Unknown',
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            type: typeof error,
            isError: error instanceof Error,
            isNetworkError: error instanceof TypeError && error instanceof Error && error.message.includes('fetch')
        };
        console.error('Detailed error in loadInventoryData:', JSON.stringify(errorInfo, null, 2));
        throw error;
    }
}; 